import _ from 'lodash';
import { LIGHT_PROTOCOL_VERSION, channelKey, lightCatalogErrors, lightCycle, evaluateProfile } from './light_profiles';
import { lightCatalogPayload } from './light_channels_transaction';
import { resolveLightProfile, evaluateAnalyticLightProfile } from './light_profile_source';
import { phaseLightingCatalog } from './light_phase_utils';
import { scheduleRuntimeInfo } from './scheduling_utils';

// The simulation owns a committed global catalog just like its other settings.
// Only the SAM54 demo advertises it; GrowBase keeps its legacy lighting path.
export function syncSimulationLighting(model) {
  if (model.type !== 5155) return;
  const initialized = Boolean(model.global.light_channels);
  const catalog = model.global.light_channels ??= {
    schema_version: LIGHT_PROTOCOL_VERSION,
    revision: 1,
    advanced_zone_mask: 0,
    zones: Array.from({ length: 9 }, (_, zone) => ({ zone, master: 100, enabled: 1, shared_toplight: 0 })),
    subzones: Array.from({ length: 36 }, (_, index) => ({
      zone: Math.floor(index / 4), subzone: index % 4, master: 100,
      enabled: 0, enabled_mask: 0, visible_mask: 0, main_channel: 255
    })),
    channels: []
  };
  model.lightingZones ??= [];
  let added = false;
  for (const [id, room] of Object.entries(model.rooms)) {
    const zone = Number(id);
    if (model.lightingZones.includes(zone)) continue;
    model.lightingZones.push(zone);
    // A restored global catalog can already allocate this physical room.
    if (catalog.channels.some((channel) => channel.zone === zone)) continue;
    added = true;
    const subzone = catalog.subzones.find((entry) => entry.zone === zone && entry.subzone === 0);
    Object.assign(subzone, { enabled: 1, enabled_mask: 1, visible_mask: 1, main_channel: 0 });
    // Start with the simple program. The same editor can select custom curves.
    catalog.channels.push({ zone, subzone: 0, channel: 0, name: 'Top light',
      minimum: room.lights.lights_min, maximum: room.lights.lights_max, points: [] });
  }
  // New physical allocations invalidate any draft based on the prior catalog.
  if (initialized && added) catalog.revision = (catalog.revision + 1) >>> 0 || 1;
}

function changedLightingSettings(next, old) {
  let changed = Number(next.advanced_zone_mask !== old.advanced_zone_mask) +
    Number(next.channels.length !== old.channels.length);
  for (const [rows, keys, identity] of [
    ['zones', ['master', 'enabled', 'shared_toplight'], (row) => row.zone],
    ['subzones', ['master', 'enabled', 'enabled_mask', 'visible_mask', 'main_channel'],
      (row) => `${row.zone}:${row.subzone}`],
    ['channels', ['minimum', 'maximum', 'name', 'points'], channelKey]
  ]) {
    for (const row of next[rows]) {
      const previous = old[rows].find((entry) => identity(entry) === identity(row));
      changed += keys.filter((key) => !previous || !_.isEqual(row[key], previous[key])).length;
    }
  }
  return changed;
}

export function saveSimulationLighting(simulation, data) {
  const model = simulation.model;
  const current = model.global.light_channels;
  const requested = data.light_channels;
  const base = Number(requested?.base_revision);
  const cycles = Object.fromEntries(Object.entries(model.rooms).map(([zone, room]) =>
    [zone, lightCycle(room.timing)]));
  if (model.type !== 5155 || Number(data.id ?? data.zone) !== 254 || !current ||
      requested?.schema_version !== LIGHT_PROTOCOL_VERSION || base !== current.revision ||
      lightCatalogErrors(requested, cycles, current).length) {
    return { type: 'error', dont_log: true, timestamp: Date.now(),
      message_parts: ['light_channels_upload_failed', '254', String(base), 'controller_rejected'] };
  }
  const next = lightCatalogPayload(requested);
  const changed = changedLightingSettings(next, current);
  next.revision = (base + 1) >>> 0 || 1;
  model.global.light_channels = _.cloneDeep(next);
  return { type: 'info', dont_log: true, timestamp: Date.now(),
    message_parts: changed
      ? ['lights', `: ${changed} `, changed === 1 ? 'gc_ms_setting_accepted' : 'gc_ms_settings_accepted']
      : ['lights', ': ', 'gc_ms_nothing_changed'] };
}

export function simulationLightingValues(model, date) {
  const catalog = model.global.light_channels;
  if (!catalog) return null;
  const info = {
    schema_version: LIGHT_PROTOCOL_VERSION, capabilities: 15, storage_state: 0,
    revision: catalog.revision, advanced_zone_mask: catalog.advanced_zone_mask,
    effective_advanced_zone_mask: catalog.advanced_zone_mask,
    fallback_zone_mask: 0, phase_zone_mask: 0,
    channel_capacity: 72, point_capacity: 256, zone_capacity: 9,
    subzones_per_zone: 4, channels_per_subzone: 6,
    channel_count: catalog.channels.length,
    point_count: catalog.channels.reduce((count, channel) => count + channel.points.length, 0),
    zone_faults: Array(9).fill(0), channels: []
  };
  const minute = date.getHours() * 60 + date.getMinutes() + date.getSeconds() / 60;
  for (const [id, room] of Object.entries(model.rooms)) {
    const zone = Number(id);
    const scheduling = model.scheduleScopes?.[zone] ?? room;
    const runtime = scheduleRuntimeInfo(scheduling);
    const active = scheduling.schedule_meta.schedule_enabled && runtime?.phaseNumber &&
      scheduling.schedule_values.recipe_valid
      ? scheduling.schedule_values.scheduling_values[runtime.phaseNumber - 1] : null;
    const phase = active && scheduling.schedule_meta.schedule_lights_enabled ? active.lighting : null;
    const effective = phase ? phaseLightingCatalog(catalog, zone, phase) : catalog;
    if (phase?.schema_version === 1) info.phase_zone_mask |= 1 << zone;
    const advanced = Boolean(effective.advanced_zone_mask & (1 << zone));
    info.effective_advanced_zone_mask = advanced
      ? info.effective_advanced_zone_mask | (1 << zone)
      : info.effective_advanced_zone_mask & ~(1 << zone);
    const cycle = lightCycle(room.timing,
      active && scheduling.schedule_meta.schedule_day_night_enabled ? active : null);
    const zoneSettings = effective.zones.find((entry) => entry.zone === zone);
    const channels = effective.channels.filter((channel) => channel.zone === zone);
    const subzones = effective.subzones.filter((entry) => entry.zone === zone);
    const sourceChannels = advanced ? channels : channels.map((channel) => ({
      ...channel, points: [], _profile_off: zoneSettings.shared_toplight
        ? channel.subzone !== 0 || channel.channel !== 0
        : subzones.find((entry) => entry.subzone === channel.subzone).main_channel !== channel.channel
    }));
    for (const channel of channels) {
      const subzone = subzones.find((entry) => entry.subzone === channel.subzone);
      const shared = zoneSettings.shared_toplight && channel.subzone === 0 && channel.channel === 0;
      // Simple mode deliberately ignores stored custom curves and followers.
      const profile = resolveLightProfile(
        advanced ? channel : sourceChannels.find((entry) => channelKey(entry) === channelKey(channel)),
        sourceChannels, subzones, zoneSettings);
      const enabled = Boolean(zoneSettings.enabled && (shared || subzone.enabled) &&
        (subzone.enabled_mask & (1 << channel.channel)) && !channel._profile_off);
      const factor = zoneSettings.master * (shared ? 100 : subzone.master) / 10000;
      const peak = Math.floor(channel.maximum * factor * 10 + 0.0000001) / 10;
      let requested = 0;
      if (enabled && profile.kind !== 'off') {
        requested = profile.kind === 'custom'
          ? evaluateProfile(profile.points, cycle, minute) * channel.maximum * factor / 100
          : peak >= channel.minimum
            ? evaluateAnalyticLightProfile({ ...channel, maximum: peak }, cycle,
                room.lights.light_sunrise_duration, minute) * peak / 100 : 0;
      }
      const requestedTenths = Math.floor(requested * 10 + 0.0000001);
      const actual = requestedTenths < channel.minimum * 10 ? 0 : requestedTenths;
      info.channels.push({ zone, subzone: channel.subzone, channel: channel.channel,
        requested: requestedTenths, actual,
        flags: (enabled ? 1 : 0) | (enabled && advanced && profile.kind === 'custom' ? 2 : 0) |
          (requestedTenths && !actual ? 16 : 0) });
    }
  }
  for (const channel of catalog.channels)
    if (!info.channels.some((entry) => channelKey(entry) === channelKey(channel)))
      info.channels.push({ zone: channel.zone, subzone: channel.subzone, channel: channel.channel,
        requested: 0, actual: 0, flags: 0 });
  return info;
}
